// Single source of truth for the page content used by all layout versions
// (/version1 … /version4) and the shared team/footer/header. Texts live per
// language in de.ts / en.ts (German is the reference and mirrors the root
// content.json); images and layout metadata live here, language-agnostic.

import useLocales from '../locales/useLocales';
//
import de from './de';
import en from './en';
import { HeroText, SectionText, UIText } from './types';

// ----------------------------------------------------------------------

export type Leistung = SectionText & {
  id: string;
  image: string;
  imagePosition: string;
};

export type Hero = HeroText & { image: string };

export type Ansatz = SectionText & { image: string; imagePosition: string };

export type Content = {
  HERO: Hero;
  ANSATZ: Ansatz;
  LEISTUNGEN: Leistung[];
  UI: UIText;
};

// ----------------------------------------------------------------------

const STOCK = '/assets/images/fpv_stock_footage';

const HERO_META = {
  image: '/assets/images/wheatfield_loop3.gif',
};

const ANSATZ_META = {
  image: `${STOCK}/Felix_Keynote_2.jpeg`,
  // speaker on stage between the two projection screens
  imagePosition: '52% 35%',
};

// imagePosition holds each photo's measured focal point (the spot that must
// stay visible). Percentage object-position pins that image point to the same
// relative spot of the container, so the subject survives cover-cropping at
// every aspect ratio from 4K banners to mobile cards.
const LEISTUNGEN_META = [
  {
    id: 'strategy-consulting',
    image: `${STOCK}/felix_keynote_1.jpg`,
    // Felix's face, upper-left third
    imagePosition: '40% 28%',
  },
  {
    id: 'training-workshops',
    image: `${STOCK}/marius_workshop_1.jpg`,
    // trainer + screen band, right of center, slightly below middle
    imagePosition: '55% 25%',
  },
  {
    id: 'keynotes',
    image: `${STOCK}/alischa_keynote.jpeg`,
    // Alischa's face, upper center
    imagePosition: '50% 24%',
  },
  {
    id: 'diy-workshop',
    image: `${STOCK}/marius_DIY.png`,
    // video screen with Marius, right of center in the upper half
    imagePosition: '68% 32%',
  },
  {
    id: 'coaching',
    image: `${STOCK}/felix_workshop_1.jpg`,
    // Felix's face, upper right
    imagePosition: '60% 25%',
  },
  {
    id: 'data-analytics',
    image: `${STOCK}/bleif_BFH_vortrag_2.jpg`,
    // speaker's face sits at ~53% of this tall portrait photo — anchoring the
    // crop window there keeps it visible at every container aspect ratio
    imagePosition: '50% 53%',
  },
  {
    id: 'assessment-center',
    // close-up of writing hands, centered
    image: `${STOCK}/FPV_schulung.jpg`,
    imagePosition: '50% 50%',
  },
];

// ----------------------------------------------------------------------

const TEXTS = { de, en };

export default function useContent(): Content {
  const { currentLang } = useLocales();

  const texts = TEXTS[currentLang.value as keyof typeof TEXTS] ?? de;

  return {
    HERO: { ...HERO_META, ...texts.hero },
    ANSATZ: { ...ANSATZ_META, ...texts.ansatz },
    LEISTUNGEN: LEISTUNGEN_META.map((meta) => ({ ...meta, ...texts.leistungen[meta.id] })),
    UI: texts.ui,
  };
}
